import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { getUser, deleteUser, type User } from '@/api/users'
import { findActiveMembership, listMemberships, type Membership, type MembershipWithPlan } from '@/api/memberships'
import { getEvents, type AccessEvent } from '@/api/events'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'
import { FaceAvatar } from '@/components/shared/FaceAvatar'
import { FaceUploadModal } from '@/components/shared/FaceUploadModal'
import { PersonalDataTab, type PersonalDataTabHandle } from './tabs/PersonalDataTab'
import { AccessDevicesTab } from './tabs/AccessDevicesTab'
import { BiometricosTab } from './tabs/BiometricosTab'
import { MembershipForm } from '@/pages/memberships/MembershipForm'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { AttentionTag, MemberCategoryTag } from './MemberList'

function formatDate(d: string) {
  return new Date(d.slice(0, 10) + 'T12:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })
}
function formatDateShort(d: string) {
  return new Date(d.slice(0, 10) + 'T12:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })
}
function daysLeft(endDate: string) {
  return Math.ceil((new Date(endDate.slice(0, 10) + 'T12:00:00').getTime() - new Date().setHours(0, 0, 0, 0)) / 86400000)
}
function timeAgo(d: string) {
  const diff = Math.floor((Date.now() - new Date(d).getTime()) / 60000)
  if (diff < 60) return `hace ${diff}m`
  if (diff < 1440) return `hace ${Math.floor(diff / 60)}h`
  return `hace ${Math.floor(diff / 1440)}d`
}

function statusCfg(status: string, isOverdue: boolean) {
  if (isOverdue || status === 'expired') return { bg: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400', dot: 'bg-red-400', label: 'Vencida' }
  if (status === 'active') return { bg: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-400', label: 'Activa' }
  if (status === 'suspended') return { bg: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50 text-amber-700 dark:text-amber-400', dot: 'bg-amber-400', label: 'Suspendida' }
  return { bg: 'bg-surface border-border text-text-muted', dot: 'bg-text-muted', label: 'Cancelada' }
}

export function MemberDetail() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { proxyUrl, proxyToken, isElectron } = useProxyConfig()

  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [membership, setMembership] = useState<MembershipWithPlan | null>(null)
  const [membershipLoading, setMembershipLoading] = useState(true)
  const [allMemberships, setAllMemberships] = useState<MembershipWithPlan[]>([])
  const [historyOpen, setHistoryOpen] = useState(false)
  const [recentEvents, setRecentEvents] = useState<AccessEvent[]>([])
  const [membershipFormOpen, setMembershipFormOpen] = useState(false)
  const [membershipFormMode, setMembershipFormMode] = useState<'create' | 'renew' | 'edit' | 'correct-start'>('create')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [faceModalOpen, setFaceModalOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<string>(searchParams.get('tab') ?? 'datos')
  const [savingData, setSavingData] = useState(false)
  const personalDataRef = useRef<PersonalDataTabHandle>(null)

  async function handleSaveData() {
    setSavingData(true)
    try {
      await personalDataRef.current?.save()
    } finally {
      setSavingData(false)
    }
  }

  useEffect(() => {
    if (!id) return
    getUser(id).then(r => setUser(r.user)).catch(() => toast.error('No se encontró el miembro')).finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    if (!id) return
    findActiveMembership(id)
      .then(r => setMembership(r.membership))
      .catch(() => {})
      .finally(() => setMembershipLoading(false))
    listMemberships(id)
      .then(r => setAllMemberships(r.memberships as MembershipWithPlan[]))
      .catch(() => {})
  }, [id])

  useEffect(() => {
    if (!user) return
    getEvents({ employeeNo: user.employee_no, limit: 5 }).then(r => setRecentEvents(r.events)).catch(() => {})
  }, [user])

  async function handleDelete() {
    if (!id) return
    try { await deleteUser(id); toast.success('Miembro eliminado'); navigate('/members') }
    catch { toast.error('Error al eliminar miembro') }
  }

  function reloadMembership() {
    if (!id) return
    findActiveMembership(id).then(r => setMembership(r.membership)).catch(() => {})
    listMemberships(id).then(r => setAllMemberships(r.memberships as MembershipWithPlan[])).catch(() => {})
  }

  if (loading) return <p className="text-text-muted text-sm p-4">Cargando...</p>
  if (!user) return <p className="text-text-muted text-sm p-4">Miembro no encontrado</p>

  const today = new Date().toISOString().split('T')[0]
  const isExpired = membership && membership.end_date < today
  const days = membership ? daysLeft(membership.end_date) : 0
  const noAccess = !user.face_uploaded && !user.card_no && !user.pin

  const SECTIONS = [
    { key: 'datos',        label: 'Datos' },
    { key: 'acceso',       label: 'Acceso' },
    { key: 'dispositivos', label: 'Dispositivos' },
    { key: 'eventos',      label: 'Eventos' },
  ]

  return (
    <div className="flex flex-col gap-4">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/members')} className="text-text-muted hover:text-text-primary transition-colors"><ArrowLeft className="w-4 h-4" /></button>
          <span className="text-text-muted text-sm">Miembros</span>
          <span className="text-text-muted text-sm">/</span>
          <span className="text-text-primary text-sm font-semibold">{user.name}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSaveData} disabled={savingData} className="flex items-center gap-1.5 text-xs bg-accent text-background font-semibold px-3 py-1.5 rounded-md hover:bg-accent-hover disabled:opacity-60 transition-colors">
            <Save className="w-3 h-3" /> {savingData ? 'Guardando...' : 'Guardar'}
          </button>
          <button onClick={() => setDeleteOpen(true)} className="flex items-center gap-1.5 text-xs border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 px-3 py-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors">
            <Trash2 className="w-3 h-3" /> Eliminar
          </button>
        </div>
      </div>

      {/* Main 2-column layout */}
      <div className="grid grid-cols-[300px_1fr] gap-5 items-start">

        {/* ── LEFT SIDEBAR ── */}
        <div className="flex flex-col gap-4">

          {/* Profile card */}
          <div className="bg-surface border border-border rounded-xl p-5 flex flex-col items-center gap-3 text-center">
            {/* Face photo — large */}
            <div className="relative">
              <FaceAvatar
                userId={user.id}
                name={user.name}
                hasFace={user.face_uploaded}
                proxyUrl={proxyUrl ?? undefined}
                proxyToken={proxyToken ?? undefined}
                size="xl"
                onOpenUpload={isElectron && proxyUrl && proxyToken ? () => setFaceModalOpen(true) : undefined}
              />
              {user.face_uploaded && (
                <span className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-surface" title="Con foto" />
              )}
            </div>

            <div>
              <div className="flex items-center justify-center gap-1.5">
                <h2 className="text-text-primary font-bold text-base leading-tight">{user.name}</h2>
                <MemberCategoryTag category={user.member_category} />
                {user.needs_attention && <AttentionTag />}
              </div>
              <p className="text-text-muted text-xs mt-0.5">CC {user.employee_no}</p>
            </div>

            {/* Membership status pill */}
            <span className={`text-xs font-medium px-3 py-1 rounded-full border ${
              isExpired ? 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400' :
              membership ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400' :
              'bg-surface border-border text-text-muted'
            }`}>
              {isExpired ? '⚠ Membresía vencida' : membership ? '● Membresía activa' : 'Sin membresía'}
            </span>

            {/* Access methods */}
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <MethodPill active={user.face_uploaded} label="Foto" />
              <MethodPill active={!!user.card_no} label="Tarjeta" />
              <MethodPill active={!!user.pin} label="PIN" />
            </div>

            {noAccess && (
              <p className="text-amber-700 dark:text-amber-400 text-[10px] bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-md px-2 py-1 w-full">
                ⚠ Sin métodos de acceso configurados
              </p>
            )}
          </div>

          {/* Membership card */}
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs font-bold uppercase tracking-wide mb-3">Membresía actual</p>
            {membershipLoading ? (
              <div className="space-y-2"><Skeleton className="h-4 w-32" /><Skeleton className="h-4 w-24" /></div>
            ) : membership ? (
              <MembershipCard
                membership={membership}
                isExpired={!!isExpired}
                days={days}
                onEdit={() => { setMembershipFormMode('edit'); setMembershipFormOpen(true) }}
                onRenew={() => { setMembershipFormMode('renew'); setMembershipFormOpen(true) }}
                onCorrect={() => { setMembershipFormMode('correct-start'); setMembershipFormOpen(true) }}
              />
            ) : (
              <div className="flex flex-col items-center gap-2 py-2">
                <p className="text-text-muted text-xs">Sin membresía activa</p>
                <button onClick={() => { setMembershipFormMode('create'); setMembershipFormOpen(true) }}
                  className="text-xs bg-accent text-background font-semibold px-4 py-2 rounded-md hover:bg-accent-hover transition-colors w-full">
                  + Nueva membresía
                </button>
              </div>
            )}
          </div>

          {/* Membership history */}
          {allMemberships.length > 0 && (
            <div className="bg-surface border border-border rounded-xl overflow-hidden">
              <button
                onClick={() => setHistoryOpen(v => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-background/40 transition-colors"
              >
                <span className="text-text-muted text-xs font-bold uppercase tracking-wide">
                  Historial de membresías
                  <span className="ml-1.5 text-text-muted/60 normal-case font-normal">({allMemberships.length})</span>
                </span>
                {historyOpen ? <ChevronUp className="w-3.5 h-3.5 text-text-muted" /> : <ChevronDown className="w-3.5 h-3.5 text-text-muted" />}
              </button>

              {historyOpen && (
                <div className="border-t border-border divide-y divide-border/50">
                  {allMemberships.map(m => {
                    const over = m.end_date < today
                    const cfg = statusCfg(m.status, over)
                    return (
                      <div key={m.id} className="px-4 py-2.5 flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-text-primary text-xs font-medium truncate">{m.plan_name}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 ${cfg.bg}`}>{cfg.label}</span>
                        </div>
                        <p className="text-text-muted text-[10px]">
                          {formatDateShort(m.start_date)} → {formatDateShort(m.end_date)}
                        </p>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── RIGHT CONTENT ── */}
        <div className="flex flex-col gap-4">
          {/* Section tabs */}
          <div className="flex border-b border-border gap-1">
            {SECTIONS.map(s => (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeSection === s.key ? 'border-accent text-accent' : 'border-transparent text-text-muted hover:text-text-primary'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Datos personales — stays mounted (just hidden) when another tab is
              active, so edits in progress aren't lost switching tabs, and the
              persistent Guardar button in the header always has something to save. */}
          <div className={`bg-surface border border-border rounded-xl p-5 ${activeSection === 'datos' ? '' : 'hidden'}`}>
            <PersonalDataTab ref={personalDataRef} user={user} onUpdated={setUser} />
          </div>

          {/* Acceso / biométricos */}
          {activeSection === 'acceso' && (
            <div className="bg-surface border border-border rounded-xl p-5">
              {isElectron && proxyUrl && proxyToken ? (
                <BiometricosTab user={user} proxyUrl={proxyUrl} proxyToken={proxyToken} onUserUpdated={u => { setUser(u) }} />
              ) : (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <span className="text-3xl">🖥</span>
                  <p className="text-text-primary text-sm font-semibold">Requiere la app local</p>
                  <p className="text-text-muted text-xs max-w-xs">Los biométricos se configuran con el dispositivo físico conectado.</p>
                </div>
              )}
            </div>
          )}

          {/* Dispositivos */}
          {activeSection === 'dispositivos' && (
            <div className="bg-surface border border-border rounded-xl p-5">
              <AccessDevicesTab userId={id!} />
            </div>
          )}

          {/* Eventos recientes */}
          {activeSection === 'eventos' && (
            <div className="bg-surface border border-border rounded-xl p-5">
              <p className="text-text-muted text-xs font-bold uppercase tracking-wide mb-3">Últimos accesos</p>
              {recentEvents.length === 0 ? (
                <p className="text-text-muted text-xs py-4 text-center">Sin eventos registrados</p>
              ) : (
                <div className="flex flex-col divide-y divide-border/50">
                  {recentEvents.map(ev => (
                    <div key={ev.id} className="flex items-center justify-between py-2 text-xs">
                      <span className={ev.event_type === 'ACCESS_GRANTED' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                        {ev.event_type === 'ACCESS_GRANTED' ? '✓' : '✗'} {ev.door_no ?? 'Entrada'}
                      </span>
                      <span className="text-text-muted">{timeAgo(ev.occurred_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Membership form modal */}
      {membershipFormOpen && (
        <MembershipForm
          open={membershipFormOpen}
          mode={membershipFormMode}
          userId={id}
          membership={membership}
          onClose={() => setMembershipFormOpen(false)}
          onSaved={(_s: Membership) => { reloadMembership(); setMembershipFormOpen(false) }}
        />
      )}

      {/* Face photo modal */}
      {isElectron && proxyUrl && proxyToken && (
        <FaceUploadModal
          open={faceModalOpen}
          onClose={() => setFaceModalOpen(false)}
          userId={user.id}
          userName={user.name}
          proxyUrl={proxyUrl}
          proxyToken={proxyToken}
          hasFace={user.face_uploaded}
          onUploaded={() => { setUser({ ...user, face_uploaded: true }); setFaceModalOpen(false) }}
        />
      )}

      {/* Delete confirm */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-sm">
          <DialogHeader><DialogTitle>Eliminar miembro</DialogTitle></DialogHeader>
          <p className="text-text-muted text-sm mt-2">¿Eliminar a <strong className="text-text-primary">{user.name}</strong>? Esta acción no se puede deshacer.</p>
          <DialogFooter className="mt-4">
            <button onClick={() => setDeleteOpen(false)} className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors">Cancelar</button>
            <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors">Eliminar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Membership card sub-component ───────────────────────────────────────────

interface MembershipCardProps {
  membership: MembershipWithPlan
  isExpired: boolean
  days: number
  onEdit: () => void
  onRenew: () => void
  onCorrect: () => void
}

function MembershipCard({ membership, isExpired, days, onEdit, onRenew, onCorrect }: MembershipCardProps) {
  const status = membership.status as string
  const isSuspended = status === 'suspended' || status === 'cancelled'

  // Progress bar (0–100) — 0% = just started, 100% = expired
  const total = new Date(membership.end_date).getTime() - new Date(membership.start_date).getTime()
  const elapsed = Date.now() - new Date(membership.start_date).getTime()
  const progress = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)))

  // Derive urgency state
  const state: 'expired' | 'urgent' | 'soon' | 'active' | 'inactive' =
    isExpired || isSuspended ? (isSuspended ? 'inactive' : 'expired')
    : days <= 3  ? 'urgent'
    : days <= 10 ? 'soon'
    : 'active'

  const barColor =
    state === 'expired'  ? 'bg-red-500'
    : state === 'urgent' ? 'bg-red-400'
    : state === 'soon'   ? 'bg-amber-400'
    : state === 'inactive' ? 'bg-text-muted'
    : 'bg-emerald-500'

  const renewLabel =
    state === 'expired'  ? 'Renovar ahora'
    : state === 'urgent' ? 'Renovar — vence pronto'
    : state === 'soon'   ? 'Renovar anticipado'
    : state === 'inactive' ? 'Reactivar'
    : 'Agregar siguiente periodo'

  const renewStyle =
    state === 'expired' || state === 'inactive'
      ? 'bg-accent text-background font-bold hover:bg-accent-hover'
      : state === 'urgent'
      ? 'bg-red-600 text-white font-bold hover:bg-red-700'
      : state === 'soon'
      ? 'bg-amber-500 text-background font-semibold hover:bg-amber-600'
      : 'border border-border text-text-muted hover:text-text-primary hover:bg-background'

  return (
    <div className="flex flex-col gap-3">
      {/* Plan name + status hint */}
      <div className="flex items-start justify-between gap-2">
        <p className="text-text-primary font-semibold text-sm leading-tight">{membership.plan_name}</p>
        {state === 'urgent' && <span className="text-[10px] text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 px-1.5 py-0.5 rounded-full shrink-0">Vence en {days}d</span>}
        {state === 'soon'   && <span className="text-[10px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 px-1.5 py-0.5 rounded-full shrink-0">Vence en {days}d</span>}
        {state === 'expired' && <span className="text-[10px] text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 px-1.5 py-0.5 rounded-full shrink-0">Vencida</span>}
        {state === 'inactive' && <span className="text-[10px] text-text-muted bg-surface border border-border px-1.5 py-0.5 rounded-full shrink-0 capitalize">{membership.status}</span>}
      </div>

      {/* Dates */}
      <div className="flex flex-col gap-1 text-xs">
        <div className="flex justify-between">
          <span className="text-text-muted">Inicio</span>
          <span className="text-text-primary">{formatDate(membership.start_date)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-text-muted">Vence</span>
          <span className={isExpired ? 'text-red-600 dark:text-red-400 font-semibold' : days <= 10 ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-text-primary'}>
            {formatDate(membership.end_date)}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1 bg-border rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${progress}%` }} />
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-1.5 mt-0.5">
        <button onClick={onRenew} className={`w-full text-xs py-2 rounded-lg transition-colors ${renewStyle}`}>
          {renewLabel}
        </button>
        <div className="flex gap-1.5">
          <button onClick={onEdit} className="flex-1 text-[10px] border border-border text-text-muted py-1.5 rounded-md hover:bg-background hover:text-text-primary transition-colors">
            Editar
          </button>
          <button onClick={onCorrect} className="flex-1 text-[10px] border border-border text-text-muted py-1.5 rounded-md hover:bg-background hover:text-text-primary transition-colors">
            Corregir inicio
          </button>
        </div>
      </div>
    </div>
  )
}

function MethodPill({ active, label }: { active: boolean; label: string }) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
      active ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400' : 'bg-transparent border-border text-text-muted opacity-40'
    }`}>
      {label}
    </span>
  )
}
